// src/pages/User/VacationForm.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { FaDownload, FaEye } from "react-icons/fa";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import LoadingOverlay from "../../components/LoadingOverlay";
import { buildProtectedUploadUrl } from "../../utils/secureFileAccess";
import logo from "../../images/logo.png";

const VacationForm = () => {
  const [file, setFile] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [vacationTypes, setVacationTypes] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [form, setForm] = useState({
    vf_type: "",
    vf_manager: "",
    vf_from_date: "",
    vf_to_date: "",
  });

  const [editingRow, setEditingRow] = useState(null); // Track which row is being edited
  const [editData, setEditData] = useState({}); // Track editable values
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const VACATION_TASK_ID = 1;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchFileBlob = useCallback(
    async (url) => {
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`File request failed with status ${response.status}`);
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(
          errorPayload?.message || "File endpoint returned JSON instead of a file",
        );
      }

      return response.blob();
    },
    [getAuthHeaders],
  );

  const handleFileAction = useCallback(
    async (rawUrl, mode, filename) => {
      const securedUrl = buildProtectedUploadUrl(rawUrl, { mode, filename });

      try {
        const blob = await fetchFileBlob(securedUrl);
        const blobUrl = URL.createObjectURL(blob);

        if (mode === "view") {
          window.open(blobUrl, "_blank", "noopener,noreferrer");
          window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
          return;
        }

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename || "download";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } catch (err) {
        console.error(`Failed to ${mode} file`, err);
        window.alert(`Failed to ${mode} file. Please try again.`);
      }
    },
    [fetchFileBlob],
  );

  // ✅ Fix timezone shift by parsing date-only strings safely
  const toInputDate = (dateString) => {
    if (!dateString) return "";
    // Extract only the date portion (YYYY-MM-DD)
    return dateString.split("T")[0];
  };

  const fetchVacationForms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/vacationform/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setUploadedFiles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, userId]);

  const fetchDropdowns = useCallback(async () => {
    try {
      const [vtRes, mgrRes] = await Promise.all([
        axios.get(`${API_BASE}/api/dropdowns/vacation-type`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        axios.get(`${API_BASE}/api/dropdowns/recruiters`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);
      setVacationTypes(vtRes.data);
      setRecruiters(mgrRes.data);
    } catch (err) {
      console.error("Dropdown fetch failed", err);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchVacationForms();
    fetchDropdowns();
  }, [fetchVacationForms, fetchDropdowns]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "application/vnd.ms-outlook", "application/x-msg", "application/CDFV2", "application/x-ole-storage", "application/octet-stream", ""];

    if (allowedTypes.includes(selected.type)) {
      setFile(selected);
    } else {
      alert("Incorrect file format.");
      setFile(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fromDate = new Date(form.vf_from_date);
    const toDate = new Date(form.vf_to_date);
    if (fromDate > toDate) {
      alert("From Date must be earlier than To Date.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    formData.append("vf_type", form.vf_type);
    formData.append("vf_manager", form.vf_manager);
    formData.append("vf_from_date", form.vf_from_date);
    formData.append("vf_to_date", form.vf_to_date);

    try {
      await axios.post(
        `${API_BASE}/api/vacationform/upload?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      alert("✔️ Vacation form uploaded successfully!");
      setFile(null);
      await fetchVacationForms();
    } catch (error) {
      console.error("Upload error:", error);
      alert(error.response?.data?.message || "Upload failed. Please try again.");
    }
  };

  const handleReviewToggle = async (vf_id, checked) => {
    try {
      setLoading(true); // 👈 SHOW loading immediately

      if (checked) {
        const confirmReady = window.confirm(
          "Are you sure you want to mark this as Ready for Review? You won’t be able to uncheck it afterward."
        );
        if (!confirmReady) return;

        await axios.put(
          `${API_BASE}/api/vacationform/review/${vf_id}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        alert("✔️ This form is now marked as Ready for Review.");
      } else {
        if (!isAdmin) {
          alert("❌ You cannot uncheck after marking Ready for Review.");
          return;
        }

        const confirmReset = window.confirm(
          "Admin: Do you want to move this form back to Pending?"
        );
        if (!confirmReset) return;

        await axios.put(
          `${API_BASE}/api/vacationform/review/reset/${vf_id}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        alert("✅ The form has been reset to Pending.");
      }

      // ✅ IMPORTANT: wait for fresh data BEFORE hiding loader
      await fetchVacationForms();
    } catch (err) {
      console.error("❌ Failed to update review status", err);
      alert("Failed to update review status. Please try again.");
    } finally {
      setLoading(false); // 👈 ALWAYS hide loader
    }
  };

  // Cancel editing (discard changes)
  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditData({});
  };

  const handleCancelForm = async (vf_id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to cancel this vacation request? This cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE}/api/vacationform/cancel/${vf_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      alert("🗑️ Vacation request canceled successfully");
      fetchVacationForms();
    } catch (err) {
      console.error("❌ Cancel failed:", err);
      alert(
        err.response?.data?.message ||
          "Failed to cancel vacation form. Please try again."
      );
    }
  };

  // ✅ Handle Edit / Save logic
  const handleEditClick = (f) => {
    setEditingRow(f.vf_id);
    setEditData({
      vf_from_date: f.vf_from_date,
      vf_to_date: f.vf_to_date,
      vf_manager: f.manager_id,
      vf_form: null,
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileEditChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setEditData((prev) => ({ ...prev, vf_form: selected }));
    }
  };

  const handleSaveClick = async (vf_id) => {
    const formData = new FormData();
    formData.append("vf_from_date", toInputDate(editData.vf_from_date));
    formData.append("vf_to_date", toInputDate(editData.vf_to_date));
    formData.append("vf_manager", editData.vf_manager);
    if (editData.vf_form) formData.append("vf_form", editData.vf_form);

    try {
      await axios.put(
        `${API_BASE}/api/vacationform/update/${vf_id}?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      alert("✔️ Vacation form updated successfully!");
      setEditingRow(null);
      fetchVacationForms();
    } catch (err) {
      console.error("❌ Update failed:", err);
      alert(
        err.response?.data?.message || "Failed to update vacation form.",
      );
    }
  };

  return (
    <>
      {loading && <LoadingOverlay text="Saving changes..." />}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          Vacation Form
        </span>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Vacation Form</h2>
          </div>
          <CompanyAddress />
        </div>
        <form onSubmit={handleSubmit} className="mt-3">
          {/* Upload new form section */}
          <div className="row mb-3">
            <div className="col-md-3">
              <label>Vacation Type *</label>
              <select
                className="form-select"
                name="vf_type"
                value={form.vf_type}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {vacationTypes.map((vt) => (
                  <option key={vt.vt_id} value={vt.vt_id}>
                    {vt.vt_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label>Manager *</label>
              <Select
                name="vf_manager"
                value={
                  recruiters.find(
                    (r) => r.value === parseInt(form.vf_manager)
                  ) || null
                }
                onChange={(selected) =>
                  setForm((prev) => ({
                    ...prev,
                    vf_manager: selected?.value || "",
                  }))
                }
                options={recruiters}
                placeholder="Select Manager"
                isSearchable
                required
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-3">
              <label>From Date *</label>
              <input
                type="date"
                name="vf_from_date"
                className="form-control"
                value={form.vf_from_date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-3">
              <label>To Date *</label>
              <input
                type="date"
                name="vf_to_date"
                className="form-control"
                value={form.vf_to_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="mb-3">
            <label>Select PDF file:</label>
            <input
              type="file"
              className="form-control"
              accept=".pdf,.jpg,.jpeg,.png,.msg"
              onChange={handleFileChange}
            />
          </div>

          <div className="d-flex justify-content-end">
            <button type="submit" className="btn btn-primary">
              Upload
            </button>
          </div>
        </form>

        {/* ✅ Vacation List */}
        {uploadedFiles.length > 0 && (
          <div className="mt-4 p-3 bg-light rounded border">
            <div className="text-muted mb-2">Vacation Requests</div>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Employee ID</th>
                  <th>Employee</th>
                  <th>Manager</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Submitted Date</th>
                  <th>Attachment</th>
                  <th>Status</th>
                  <th>Ready for Review</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {uploadedFiles.map((f, index) => (
                  <tr
                    key={f.vf_id}
                    className={
                      f.vf_status === "completed"
                        ? "table-secondary text-muted"
                        : f.vf_status === "review"
                        ? "table-warning"
                        : ""
                    }
                  >
                    <td>{index + 1}</td>
                    <td>{f.userid_no}</td>
                    <td>{f.employee_name}</td>
                    <td>
                      {editingRow === f.vf_id ? (
                        <Select
                          value={
                            recruiters.find(
                              (r) => r.value === Number(editData.vf_manager)
                            ) || null
                          }
                          onChange={(selected) =>
                            setEditData((prev) => ({
                              ...prev,
                              vf_manager: selected?.value || "",
                            }))
                          }
                          options={recruiters}
                          isSearchable
                          placeholder="Select Manager"
                        />
                      ) : (
                        f.manager_name || "—"
                      )}
                    </td>

                    <td>
                      {editingRow === f.vf_id ? (
                        <input
                          type="date"
                          name="vf_from_date"
                          value={toInputDate(editData.vf_from_date)}
                          onChange={handleEditChange}
                          className="form-control form-control-sm"
                        />
                      ) : (
                        toInputDate(f.vf_from_date)
                      )}
                    </td>

                    <td>
                      {editingRow === f.vf_id ? (
                        <input
                          type="date"
                          name="vf_to_date"
                          value={toInputDate(editData.vf_to_date)}
                          onChange={handleEditChange}
                          className="form-control form-control-sm"
                        />
                      ) : (
                        toInputDate(f.vf_to_date)
                      )}
                    </td>

                    <td>{toInputDate(f.vf_submitted_date)}</td>
                    <td>
                      {editingRow === f.vf_id ? (
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.msg"
                          onChange={handleFileEditChange}
                        />
                      ) : f.vf_form ? (
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
                            title="View file"
                            onClick={() =>
                              handleFileAction(
                                `${API_BASE}/uploads/vacationforms/${f.vf_form}`,
                                "view",
                                f.vf_form_original || f.vf_form,
                              )
                            }
                          >
                            <FaEye />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
                            title="Download file"
                            onClick={() =>
                              handleFileAction(
                                `${API_BASE}/uploads/vacationforms/${f.vf_form}`,
                                "download",
                                f.vf_form_original || f.vf_form,
                              )
                            }
                          >
                            <FaDownload />
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted">No file</span>
                      )}
                    </td>

                    <td>
                      {f.vf_status === "not_completed"
                        ? "Pending"
                        : f.vf_status === "review"
                        ? "Under Review"
                        : "Reviewed"}
                    </td>

                    <td>
                      <input
                        type="checkbox"
                        checked={f.vf_status !== "not_completed"}
                        disabled={
                          loading ||
                          f.vf_status === "completed" || // 👈 once reviewed, never touch again
                          (!isAdmin && f.vf_status !== "not_completed")
                        }
                        onChange={(e) =>
                          handleReviewToggle(f.vf_id, e.target.checked)
                        }
                      />
                    </td>

                    <td>
                      {editingRow === f.vf_id ? (
                        <>
                          <button
                            className="btn btn-sm btn-success me-2"
                            onClick={() => handleSaveClick(f.vf_id)}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn-sm btn-primary me-2"
                            disabled={
                              !isAdmin && f.vf_status !== "not_completed"
                            }
                            onClick={() => handleEditClick(f)}
                          >
                            Edit
                          </button>

                          {f.vf_status === "not_completed" ? (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleCancelForm(f.vf_id)}
                            >
                              Delete
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-secondary"
                              disabled
                            >
                              Locked
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Review Manager Section */}
        <ReviewManager
          taskName="vacation_form"
          taskId={VACATION_TASK_ID}
          userId={userId}
          isAdmin={isAdmin}
          referenceId={uploadedFiles[0]?.vf_id || null}
          onReviewed={async () => {
            setLoading(true);
            await fetchVacationForms(); // 👈 refresh table
            setLoading(false);
          }}
          setGlobalLoading={setLoading}
        />
      </div>
    </>
  );
};

export default VacationForm;

